import { useCallback } from 'react';
import type { History } from 'history';
import { useUpdatePreferencesMutation } from '@invision/core';
import type { Security_SessionSummary } from 'invApi';
import {
  handleSecureRedirect,
  type SecureRedirectResult
} from '../utils/redirectHelpers';
import {
  buildClearedResumePreferences,
  getResumeUrlFromSession,
  resolvePostAuthRedirectUrl
} from '../utils/resumeUrlHelpers';

interface PostAuthRedirectParams {
  /** Session summary from the authentication response. */
  sessionSummary?: Security_SessionSummary;
  /** Active business unit's system id from the authentication response. */
  systemId: string | null;
  /** Tab-scoped `sessionStorage` redirect URL (external-reference deep links). */
  storedRedirectUrl: string | null;
  /** Clears the tab-scoped `sessionStorage` redirect URL. */
  clearStoredRedirectUrl: () => void;
  /** React Router history object. */
  history: History;
  /** Context for logging (e.g. 'login', 'idp', 'session-validation'). */
  logContext?: string;
  /** Whether to add a delay for state synchronization (IDP needs this). */
  addDelay?: boolean;
}

/**
 * Resolve and perform the post-authentication redirect, honoring a server-side
 * "resume last URL" preference over the tab-scoped stored redirect.
 *
 * The server-side resume URL belongs to the authenticated user (keyed by the
 * active business unit), so it can never resume a user onto a page that
 * belonged to a different user on a shared, reused tab. Once consumed it is
 * cleared server-side so a later no-op re-login does not resume repeatedly.
 *
 * Module-access validation and the no-access / fallback outcomes are unchanged
 * — this simply chooses which URL is fed into {@link handleSecureRedirect}.
 */
export function usePostAuthRedirect() {
  const updatePreferences = useUpdatePreferencesMutation();

  return useCallback(
    async ({
      sessionSummary,
      systemId,
      storedRedirectUrl,
      clearStoredRedirectUrl,
      history,
      logContext,
      addDelay
    }: PostAuthRedirectParams): Promise<SecureRedirectResult> => {
      const resumeUrl = getResumeUrlFromSession(sessionSummary, systemId);
      const effectiveRedirectUrl = resolvePostAuthRedirectUrl(
        resumeUrl,
        storedRedirectUrl
      );

      // Clear the consumed resume preference before navigating away so a later
      // no-op re-login does not resume to the same page. Best-effort: a stale
      // preference would simply be overwritten on the next idle timeout.
      if (resumeUrl && systemId) {
        try {
          await updatePreferences.mutateAsync({
            Preferences: buildClearedResumePreferences(sessionSummary, systemId)
          });
        } catch {
          // Ignore — clearing the resume URL must never block the redirect.
        }
      }

      // Clear any tab-scoped stored redirect we did NOT feed into the redirect
      // (e.g. a stale value left by a previous user on a shared tab) so it never
      // survives this login. handleSecureRedirect clears the one it consumes.
      if (!effectiveRedirectUrl && storedRedirectUrl) {
        clearStoredRedirectUrl();
      }

      return handleSecureRedirect({
        sessionSummary,
        redirectUrl: effectiveRedirectUrl,
        clearRedirectUrl: clearStoredRedirectUrl,
        history,
        logContext,
        addDelay
      });
    },
    [updatePreferences]
  );
}
